/**
 * Pure decisions for keeping an HTMLVideoElement in step with the timeline clock.
 */

export interface PlaybackCorrection {
  /** Hard seek required (drift too large to absorb). */
  seek: boolean;
  /** Playback rate to apply (base rate, nudged slightly to absorb small drift). */
  rate: number;
}

/** Drift (seconds) above which a playing element is re-seeked. */
export function hardSyncThreshold(fps: number): number {
  return Math.max(3 / fps, 0.12);
}

/**
 * While playing: seek on large drift, otherwise nudge the playback rate by up to ±5 % so small
 * drifts converge without visible stutter.
 */
export function playbackCorrection(
  current: number,
  target: number,
  fps: number,
  baseRate: number,
): PlaybackCorrection {
  const drift = target - current; // > 0: element is behind
  if (Math.abs(drift) > hardSyncThreshold(fps)) return { seek: true, rate: baseRate };
  const soft = 1 / fps;
  if (Math.abs(drift) <= soft) return { seek: false, rate: baseRate };
  const nudge = Math.max(-0.05, Math.min(0.05, drift * 0.5));
  return { seek: false, rate: baseRate * (1 + nudge) };
}

/** While paused: seek whenever the element is not showing the target frame (half-frame tolerance). */
export function needsSeek(current: number, target: number, playing: boolean, fps: number): boolean {
  if (playing) return Math.abs(target - current) > hardSyncThreshold(fps);
  return Math.abs(target - current) > 0.5 / fps;
}

/**
 * Seek target nudged slightly forward so float rounding never lands on the previous frame. Clamped to
 * the media duration when known.
 */
export function seekTarget(time: number, mediaFps: number, duration?: number): number {
  const t = time + Math.min(0.25 / mediaFps, 0.005);
  return duration !== undefined ? Math.min(t, Math.max(0, duration - 0.001)) : t;
}

/**
 * Decides whether a sequential frame reader must restart for a requested timestamp: when going
 * backwards, or jumping further ahead than decoding forward would be worth.
 */
export function shouldResetReader(
  currentTimestamp: number | null,
  target: number,
  maxForwardGap = 2,
): boolean {
  if (currentTimestamp === null) return true;
  if (target < currentTimestamp - 1e-6) return true;
  return target - currentTimestamp > maxForwardGap;
}
